import React, { useState, useEffect } from 'react';
import { Save, RefreshCw, AlertTriangle, X, CheckCircle, Package } from 'lucide-react';
import MainService from '../../services/MainService';
import { useScannerVoucherHandlers } from './scannerVoucherInput';
import ScannerModeToggle from './ScannerModeToggle';

const BulkInScan = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [employees, setEmployees] = useState([]);
  const [formData, setFormData] = useState({
    VoucherNo: '',
    EmployeeId: '',
    IsScanner: false,
  });
  const [selectedEmployeeName, setSelectedEmployeeName] = useState('');
  const voucherHandlers = useScannerVoucherHandlers(formData.IsScanner, setFormData);

  useEffect(() => {
    loadEmployees();
  }, []);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const loadEmployees = async () => {
    try {
      const response = await MainService.EmployeesInDropDown();
      setEmployees(response || []);
    } catch (err) {
      console.error('Load employees error:', err);
      setError('Failed to load employees');
      setEmployees([]);
    }
  };

  const handleEmployeeChange = (e) => {
    const employeeId = e.target.value;
    const employee = employees.find(emp => emp.Id == employeeId);
    setFormData(prev => ({ ...prev, EmployeeId: employeeId }));
    setSelectedEmployeeName(employee?.Name || '');
  };

  const handleInit = () => {
    setFormData({
      VoucherNo: '',
      EmployeeId: '',
      IsScanner: false,
    });
    setSelectedEmployeeName('');
    setError('');
    setSuccess('');
  };

  const handleSave = async () => {
    if (!formData.VoucherNo.trim()) {
      setError('No Vouchers');
      return;
    }
    if (!formData.EmployeeId) {
      setError('No Driver Selected');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      // Split vouchers based on scanner mode
      const separator = formData.IsScanner ? ' ' : '.';
      const orders = formData.VoucherNo.split(separator).filter(v => v.trim().length > 0);

      if (orders.length === 0) {
        setError('No Vouchers');
        return;
      }

      // Build array of store objects
      const array = orders.map(voucherNo => ({
        VoucherNo: voucherNo.trim(),
        Driver: selectedEmployeeName,
        EmployeeId: formData.EmployeeId,
        Type: 'STORE IN',
      }));

      const response = await MainService.AddMultipleStores(array);
      
      if (response === 'Success' || response === true || response === 'true') {
        setSuccess('Bulk store in operation completed successfully');
        // Clear form but keep success message visible
        setFormData({
          VoucherNo: '',
          EmployeeId: '',
          IsScanner: false,
        });
        setSelectedEmployeeName('');
      } else {
        setError(response || 'Failed to save');
      }
    } catch (err) {
      console.error('Save bulk store in error:', err);
      setError('Network Error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Toast Messages */}
      {error && (
        <div className="fixed top-4 right-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError('')} className="text-red-500 hover:text-red-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="fixed top-4 right-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{success}</span>
          <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Header Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="flex items-center mb-2">
              <Package className="w-6 h-6 text-primary mr-2" />
              <h1 className="text-3xl font-bold text-gray-900">Bulk In Scan</h1>
            </div>
            <p className="text-sm text-gray-500 ml-8">Bulk store in operations</p>
          </div>
          <p className="sm:max-w-xs sm:text-right text-sm text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
            <strong>Note:</strong> Delivered vouchers will be thrown automatically.
          </p>
        </div>
      </div>

      {/* Form Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <ScannerModeToggle
          checked={formData.IsScanner}
          disabled={loading}
          onChange={(checked) =>
            setFormData((prev) => ({ ...prev, IsScanner: checked }))
          }
        />

        {/* Form Fields */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Voucher Numbers
            </label>
            <input
              type="text"
              name="VoucherNo"
              value={formData.VoucherNo}
              onChange={voucherHandlers.onChange}
              onKeyDown={voucherHandlers.onKeyDown}
              onPaste={voucherHandlers.onPaste}
              placeholder={formData.IsScanner ? "Scan / type / paste vouchers — space added after each entry" : "Enter Voucher Numbers Ex: 101.150.554.6658 etc"}
              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                formData.IsScanner
                  ? 'border-[#46955A]/50 bg-[#46955A]/5'
                  : 'border-gray-300'
              }`}
              disabled={loading}
            />
            <p className="mt-1 text-xs text-gray-500">
              {formData.IsScanner
                ? 'Spaces separate vouchers automatically in Scanner Mode'
                : 'Separate vouchers with dots (.)'}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Driver
            </label>
            <select
              name="EmployeeId"
              value={formData.EmployeeId}
              onChange={handleEmployeeChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              disabled={loading}
            >
              <option value="">Select Driver</option>
              {employees.map((emp) => (
                <option key={emp.Id} value={emp.Id}>
                  {emp.Name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-center gap-4">
          <button
            onClick={handleInit}
            disabled={loading}
            className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          <button
            onClick={handleSave}
            disabled={loading || !formData.VoucherNo.trim() || !formData.EmployeeId}
            className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {loading ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save
              </>
            )}
          </button>
        </div>

        {/* Loading Indicator */}
        {loading && (
          <div className="mt-6 flex items-center justify-center">
            <RefreshCw className="w-8 h-8 text-primary animate-spin" />
          </div>
        )}
      </div>
    </div>
  );
};

export default BulkInScan;

